import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Icon, Tag, message } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import './index.scss';

interface AlbumData {
  id?: string;
  albumName: string;
  coverArtPreview: string | null;
  artists: string[];
  genres: string[];
  moods: string[];
  caption: string;
  upc: string;
  tracks?: number;
}

interface SongData {
  id: string;
  songTitle: string;
  artists: string[];
  coverArtPreview: string | null;
  genres: string[];
  moods: string[];
  producer: string;
}

// 拖拽图标
const DragIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

const AlbumEditReorder: React.FC = () => {
  const navigate = useNavigate();

  // 从 sessionStorage 获取专辑数据
  const albumData: AlbumData | null = useMemo(() => {
    const stored = sessionStorage.getItem('albumData');
    return stored ? JSON.parse(stored) : null;
  }, []);

  // 从 sessionStorage 获取歌曲列表
  const [songs, setSongs] = useState<SongData[]>(() => {
    const stored = sessionStorage.getItem('albumSongs');
    return stored ? JSON.parse(stored) : [];
  });

  // 拖拽状态
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // 如果没有专辑数据，返回创建页面
  useEffect(() => {
    if (!albumData) {
      navigate(ROUTES.albumCreate);
    }
  }, [albumData, navigate]);

  // 处理返回
  const handleBack = () => {
    navigate(ROUTES.dashboard);
  };

  // 移动歌曲
  const moveSong = useCallback((dragIndex: number, hoverIndex: number) => {
    setSongs(prevSongs => {
      const newSongs = [...prevSongs];
      const [draggedSong] = newSongs.splice(dragIndex, 1);
      newSongs.splice(hoverIndex, 0, draggedSong);
      // 更新 sessionStorage
      sessionStorage.setItem('albumSongs', JSON.stringify(newSongs));
      return newSongs;
    });
  }, []);

  // 拖拽开始
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  // 拖拽结束
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // 拖拽经过
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveSong(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  // 编辑歌曲
  const handleEditSong = (index: number) => {
    const song = songs[index];
    // 转换为 EditSong 需要的格式
    const songForEdit = {
      id: song.id || String(index),
      title: song.songTitle,
      album: albumData?.albumName || '',
      cover: song.coverArtPreview || albumData?.coverArtPreview || '',
      genres: song.genres || [],
      releaseDate: '',
      status: 'Under Review' as const,
      artists: song.artists || [],
      producer: song.producer || '',
      moods: song.moods || [],
    };
    // 跳转到编辑页面
    navigate(`/song/${song.id || index}/edit`, {
      state: {
        song: songForEdit,
        returnTo: ROUTES.albumEditReorder,
        returnState: {},
      },
    });
  };

  // 添加更多歌曲
  const handleAddMoreSongs = () => {
    navigate(ROUTES.releaseUpload);
  };

  // 下一步
  const handleNext = () => {
    if (songs.length === 0) {
      message.warning('Please add at least one song to the album');
      return;
    }

    // 保存歌曲列表到 sessionStorage
    sessionStorage.setItem('albumSongs', JSON.stringify(songs));
    navigate(ROUTES.albumSchedule);
  };

  if (!albumData) {
    return null;
  }

  return (
    <div className="album-edit-reorder">
      {/* 页面标题 */}
      <div className="album-edit-reorder__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Edit & Reorder Album</span>
        </button>
      </div>

      {/* 主内容 */}
      <div className="album-edit-reorder__content">
        <div className="album-edit-reorder__card">
          {/* 标题 */}
          <h2 className="album-edit-reorder__title">Album Track List</h2>

          {/* 专辑信息卡片 */}
          <div className="album-info-card">
            <div className="album-info-card__cover">
              {albumData.coverArtPreview ? (
                <img src={albumData.coverArtPreview} alt="Album cover" />
              ) : (
                <div className="album-info-card__placeholder" />
              )}
            </div>
            <div className="album-info-card__info">
              <span className="album-info-card__name">{albumData.albumName}</span>
              <span className="album-info-card__artist">{albumData.artists[0]}</span>
            </div>
          </div>

          {/* 歌曲列表 */}
          {songs.length > 0 ? (
            <div className="track-list">
              {songs.map((song, index) => (
                <div
                  key={song.id || index}
                  className={`track-item ${draggedIndex === index ? 'track-item--dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                >
                  {/* 拖拽手柄 */}
                  <div className="track-item__drag">
                    <DragIcon />
                  </div>

                  {/* 序号 */}
                  <div className="track-item__number">
                    <span>{index + 1}</span>
                  </div>

                  {/* 歌曲信息 */}
                  <div className="track-item__info">
                    <span className="track-item__title">{song.songTitle || 'Untitled'}</span>
                    <span className="track-item__artist">{song.artists?.[0] || 'Unknown Artist'}</span>
                  </div>

                  {/* 编辑按钮 */}
                  <button
                    className="track-item__edit"
                    onClick={() => handleEditSong(index)}
                  >
                    <Icon style="Edit" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No songs added yet. Click "Add More Songs" to get started.</p>
            </div>
          )}

          {/* 按钮区域 */}
          <div className="album-edit-reorder__actions">
            {/* 添加更多歌曲按钮 */}
            <button className="add-songs-btn" onClick={handleAddMoreSongs}>
              <Icon style="Upload" />
              <span>Add More Songs</span>
            </button>

            {/* 下一步按钮 */}
            <Button
              type="primary"
              onClick={handleNext}
              className="album-edit-reorder__submit"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlbumEditReorder;
